import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Button,
  CopyButton,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableHeaderProps,
  Icon,
  useSidePanel,
  Box as Wrapper,
} from '@node-space/ui-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { Channel } from 'types/channels'
import { CurrencyInterface } from 'types/wallets'
import { ChannelsSidePanel } from './ChannelsSidePanel'

interface ChannelsTableProps {
  channels: Channel[]
  currencies: CurrencyInterface[]
  handleOpenChannelPaymentSheet?: (channel: Channel) => void
}

const ChannelsTable = ({
  channels,
  currencies,
  handleOpenChannelPaymentSheet,
}: ChannelsTableProps) => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext()

  const {
    isSidePanelOpen,
    sidePanelData: channel,
    paging: pagingDetails,
    setIsSidePanelOpen: setIsChannelDetailsOpen,
    updateSidePanel: updateCustomerDetails,
  } = useSidePanel(channels)

  const handleRowClick = (index: number) => {
    setIsChannelDetailsOpen(true)
    updateCustomerDetails(index)
  }

  const handleCloseDetailModal = () => {
    setIsChannelDetailsOpen(false)
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return [
      { key: 'description', label: t('description'), variant: DataTableCellVariants.TEXT },
      { key: 'contact', label: t('contacts.contact'), variant: DataTableCellVariants.TEXT },
      { key: 'receivingWallet', label: t('receivingWallet'), variant: DataTableCellVariants.TEXT },
      { key: 'currency', label: t('currency'), variant: DataTableCellVariants.TEXT },
      { key: 'channelURL', label: t('channelURL'), variant: DataTableCellVariants.TEXT },
      { key: 'actions', label: '', variant: DataTableCellVariants.ACTION },
    ]
  }, [])

  const tableData = useMemo(() => {
    if (!channels) return []

    return channels?.map((channel, index) => {
      const currencyName = currencies?.find(
        currency => currency?.code === channel?.payCurrency
      )?.name

      return {
        id: channel?.reference,
        testid: channel?.reference,
        onRowClick: () => handleRowClick(index),
        cells: [
          {
            key: 'description',
            variant: DataTableCellVariants.TEXT,
            Text: channel?.reference,
          },
          {
            key: 'contact',
            variant: DataTableCellVariants.TEXT,
            ...(channel?.contact?.name
              ? {
                  Avatar: <Avatar type="entity" name={channel?.contact?.name ?? ''} />,
                  Text: channel?.contact?.name,
                }
              : {
                  Actions: [
                    <Button
                      variant="ghost"
                      noPadding
                      disabled={viewOnly}
                      key={`assign-contact-${channel?.uuid}`}
                      iconElement={<Icon name="UserCirclePlus" />}
                      onClick={e => {
                        e?.stopPropagation()
                        handleOpenChannelPaymentSheet(channel)
                      }}
                    >
                      {t('contacts.assignContact')}
                    </Button>,
                  ],
                }),
          },
          {
            key: 'receivingWallet',
            variant: DataTableCellVariants.TEXT,
            Avatar: (
              <Avatar
                type="currency"
                name={channel?.walletCurrency ? channel?.walletCurrency : ''}
              />
            ),
            Text: channel?.walletDescription,
          },
          {
            key: 'currency',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar type="currency" name={channel?.payCurrency} />,
            Text: currencyName,
            BottomText: channel?.payCurrency,
          },
          {
            key: 'channelURL',
            variant: DataTableCellVariants.ACTION,
            align: DataTableColumnAlignments.LEFT,
            Actions: [
              <CopyButton key={`copy-${channel?.uuid}`} copyText={channel?.redirectUrl}>
                {t('copy')}
              </CopyButton>,
            ],
          },
        ],
      }
    })
  }, [channels])

  return (
    <>
      <Wrapper className="overflow-x-auto">
        <DataTable headers={columns} data={tableData} />
      </Wrapper>
      <ChannelsSidePanel
        isOpen={isSidePanelOpen}
        onClose={handleCloseDetailModal}
        channelDetails={channel}
        paging={pagingDetails}
        currencies={currencies}
      />
    </>
  )
}

export default React.memo(ChannelsTable)
